import { z } from "zod";
import {
    RequestExamplesSheetDragDirection,
    RequestExamplesVariant,
} from "./requestExamples";

export const PhilanthropySearchParams = z.object({
    tenant: z.string().min(1),
    locale: z.string().min(1),
    variant: RequestExamplesVariant.default("mobile"),
    parentOrigin: z.url().optional(),
    slide: z.string().min(1).optional(),
});
export type PhilanthropySearchParams = z.infer<typeof PhilanthropySearchParams>;

export const PhilanthropyReadyMessage = z.object({
    type: z.literal("philanthropy.ready"),
});
export type PhilanthropyReadyMessage = z.infer<typeof PhilanthropyReadyMessage>;

export const PhilanthropyCloseMessage = z.object({
    type: z.literal("philanthropy.close"),
    reason: z.enum(["closeButton", "completed", "swipeUp", "swipeDown"]),
});
export type PhilanthropyCloseMessage = z.infer<typeof PhilanthropyCloseMessage>;

export const PhilanthropySheetDragMessage = z.object({
    type: z.literal("philanthropy.sheetDrag"),
    direction: RequestExamplesSheetDragDirection,
    offsetY: z.number(),
    phase: z.enum(["start", "move", "end"]),
    shouldDismiss: z.boolean().optional(),
    velocity: z.number().optional(),
});
export type PhilanthropySheetDragMessage = z.infer<typeof PhilanthropySheetDragMessage>;

export const PhilanthropyVisibilityMessage = z.object({
    type: z.literal("philanthropy.visibility"),
    visible: z.boolean(),
});
export type PhilanthropyVisibilityMessage = z.infer<
    typeof PhilanthropyVisibilityMessage
>;

/** Rewinds the slideshow; `slideId` jumps straight to that slide instead of the first. */
export const PhilanthropyResetMessage = z.object({
    type: z.literal("philanthropy.reset"),
    slideId: z.string().min(1).optional(),
});
export type PhilanthropyResetMessage = z.infer<typeof PhilanthropyResetMessage>;

export const PhilanthropyMessage = z.discriminatedUnion("type", [
    PhilanthropyReadyMessage,
    PhilanthropyCloseMessage,
    PhilanthropySheetDragMessage,
]);
export type PhilanthropyMessage = z.infer<typeof PhilanthropyMessage>;

export const PhilanthropyParentControlMessage = z.discriminatedUnion("type", [
    PhilanthropyVisibilityMessage,
    PhilanthropyResetMessage,
]);
export type PhilanthropyParentControlMessage = z.infer<
    typeof PhilanthropyParentControlMessage
>;

export function parsePhilanthropyMessageData(
    data: unknown,
): PhilanthropyMessage | null {
    const parsed = PhilanthropyMessage.safeParse(parsePostMessageData(data));
    return parsed.success ? parsed.data : null;
}

export function parsePhilanthropyParentControlMessageData(
    data: unknown,
): PhilanthropyParentControlMessage | null {
    const parsed = PhilanthropyParentControlMessage.safeParse(
        parsePostMessageData(data),
    );
    return parsed.success ? parsed.data : null;
}

function parsePostMessageData(data: unknown): unknown {
    if (typeof data !== "string") {
        return data;
    }

    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
}
